import React, {Fragment, useEffect} from 'react';
import {useRecoilValueLoadable} from 'recoil';
import {useRecoilQuery} from 'recoil-toolkit';

import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {AppointmentBookingEnum, FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {AlertActions, AlertModal} from 'presentation/components';
import {RecoilLoadableStatesEnum} from 'presentation/constants/RecoilLoadableStatesEnum';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useExternalOpenDateModalStore} from 'presentation/modules/booking/shared/store/useExternalOpenDateModalStore';

import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle} from 'presentation/utils';
import {
	BookingErrorState,
	BookingLoadingState,
	BookingNotFoundState,
	NearestDateProfessionalGrid,
	PendingDialog,
	SelectedDateProfessionalGrid,
	TherapyAlert,
} from '../../components';
import {BookingNotFoundStateVariantEnum} from '../../components/BookingNotFoundState/types';
import {DisabilityEnum, FlowEnum, PATH_TITLE} from '../../constants';
import {useBookingHourSelection} from '../../hooks/useBookingHourSelection/useBookingHourSelection';
import {bookingDatesState} from './atoms';
import {ProfessionalSelectionProps} from './types';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';

const {booking} = translations['pt-br'].others.sidebar;
const {loadingError, loadingState, errorModal, verifyingAuth, searchBestOptionModal} =
	translations['pt-br'].privateFlow.bookingFlow.professionalFlow;

export function ProfessionalSelection({
	flowType,
	handleNextStep,
	handlePrevStep,
}: ProfessionalSelectionProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {SEARCH_PARAMS} = useQueryParams();

	const setExternalOpenDateModal = useExternalOpenDateModalStore(
		state => state.setExternalOpenDateModal,
	);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);

	const {disability, calendarActive, autocompleteQuery} = SEARCH_PARAMS;

	const isCalendarActive = !!(calendarActive && calendarActive === 'true');

	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isExam = flowType === FlowEnum.EXAM;

	const {isRebooking, dateAt, isManualLocation} = bookingData;

	const {
		authModal,
		handleHourSelection,
		odontoAddressModal,
		setAuthModal,
		setOdontoAddressModal,
		openSearchBestOptionModal,
		goToTelehealthProfessionalSelection,
		goToNextStep,
		isLoadingTelehealthMostRecentDate,
	} = useBookingHourSelection({
		flowType,
		handleNext: handleNextStep,
	});

	const availableDatesParams = {
		cityName: bookingData.cityName,
		disability: disability as DisabilityEnum,
		districtName: isOdonto ? bookingData.odontoUnitDistrictSelected : bookingData.districtName,
		flowType,
		unitCode: bookingData.unitCode,
		specialtyCode: bookingData.specialtyCode,
		subSpecialtyCode: bookingData.subSpecialtyCode,
		ufCode: bookingData.ufCode,
		appointmentNumber: isTelehealth && isRebooking ? bookingData.bookingNumber : undefined,
		typeLocalName: isManualLocation ? bookingData.typeLocalName : TypeLocalNameEnum.CIDADE,
		providerId:
			(bookingData?.isFavoriteDoctorsFlow && bookingData?.professionalCode) ||
			autocompleteQuery,
	};

	const availableDatesList = useRecoilValueLoadable(bookingDatesState(availableDatesParams));

	const {refresh, loading} = useRecoilQuery(bookingDatesState(availableDatesParams));

	useEffect(() => {
		refresh();
	}, []);

	const IS_ERROR =
		availableDatesList.state === RecoilLoadableStatesEnum.HAS_ERROR &&
		availableDatesList.contents.statusCode !== HttpStatusCode.unprocessable;
	const IS_LOADING = availableDatesList.state === RecoilLoadableStatesEnum.LOADING || loading;

	useEffect(() => {
		setHideSearchBarState(Boolean(IS_ERROR));
	}, [IS_ERROR]);

	if (IS_ERROR) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrevStep}
				handleRefresh={refresh}
			/>
		);
	}

	if (IS_LOADING) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrevStep}
			/>
		);
	}

	const NOT_FOUND =
		!availableDatesList.contents ||
		availableDatesList.contents.length === 0 ||
		(availableDatesList.state === RecoilLoadableStatesEnum.HAS_ERROR &&
			availableDatesList.contents.statusCode === HttpStatusCode.unprocessable);

	if (NOT_FOUND) {
		const shoudlHideSearchBar = isOdonto || isExam;
		setHideSearchBarState(shoudlHideSearchBar);

		return (
			<BookingNotFoundState
				flowType={flowType}
				pageTitle={pageTitle}
				handleBack={handlePrevStep}
				variant={BookingNotFoundStateVariantEnum.PROFESSIONAL}
				showHeader={false}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROFISSIONAL_DISPONIVEL}
			/>
		);
	}

	return (
		<Fragment>
			{isCalendarActive && dateAt ? (
				<SelectedDateProfessionalGrid
					flowType={flowType}
					handleNext={handleHourSelection}
					handlePrev={handlePrevStep}
				/>
			) : (
				<NearestDateProfessionalGrid
					flowType={flowType}
					handleNext={handleHourSelection}
					handlePrev={handlePrevStep}
					dates={availableDatesList.contents}
				/>
			)}

			{isOdonto ? (
				<Fragment>
					<AlertModal
						headerTitle={errorModal.headerTitle}
						title={errorModal.title}
						variant="error"
						description={errorModal.description}
						open={odontoAddressModal === 'error'}
						onClose={() => setOdontoAddressModal('none')}
					/>

					<PendingDialog
						open={odontoAddressModal === 'loading'}
						onClose={() => setOdontoAddressModal('none')}
						text={verifyingAuth}
					/>
				</Fragment>
			) : (
				<Fragment>
					<TherapyAlert
						flowType={flowType}
						alertType="allScheduled"
						open={authModal === 'allScheduled'}
						setOpen={() => setAuthModal('none')}
					/>

					<TherapyAlert
						flowType={flowType}
						alertType="needAuth"
						open={authModal === 'needAth'}
						setOpen={() => setAuthModal('none')}
					/>

					<TherapyAlert
						flowType={flowType}
						alertType="minimumInterval"
						open={authModal === 'minimumInterval'}
						setOpen={() => setAuthModal('none')}
						setCalendarOpen={() => setExternalOpenDateModal(true)}
					/>

					<AlertModal
						headerTitle={errorModal.headerTitle}
						title={errorModal.title}
						variant="error"
						description={errorModal.description}
						open={authModal === 'error'}
						onClose={() => setAuthModal('none')}
					/>

					<PendingDialog
						open={authModal === 'loading'}
						onClose={() => setAuthModal('none')}
						text={verifyingAuth}
					/>

					<AlertModal
						headerTitle={searchBestOptionModal.headerTitle}
						title={searchBestOptionModal.title}
						headerDataTagId={
							AppointmentBookingEnum.VISUALIZOU_MODAL_PRESENCIAL_AO_DIGITAL
						}
						variant="info"
						description={searchBestOptionModal.description}
						open={openSearchBestOptionModal}
						disableClose
						footerContent={
							<AlertActions
								primaryText={searchBestOptionModal.primary}
								secondaryText={searchBestOptionModal.secondary}
								primaryAction={goToTelehealthProfessionalSelection}
								secondaryAction={goToNextStep}
								dataTagId={
									AppointmentBookingEnum.CLICK_NAVEGAR_TELEMEDICINA_PRESENCIAL_AO_DIGITAL
								}
								secondaryBtnDataTagId={
									AppointmentBookingEnum.CLICK_MANTER_AGENDAMENTO_PRESENCIAL_AO_DIGITAL
								}
								flexDirection="column-reverse"
							/>
						}
					/>

					<PendingDialog
						open={isLoadingTelehealthMostRecentDate}
						text={searchBestOptionModal.loading}
					/>
				</Fragment>
			)}
		</Fragment>
	);
}
